import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Loader2, Package, ShoppingCart } from 'lucide-react';
import { useTheme } from '@evi/skins';
import { confirmStoreCheckout, formatCents } from '@/lib/api';
import { useCartStore, selectItemCount } from '@/store/cart';
import styles from './Store.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Stripe Checkout return page (/store/success?session_id=cs_...).
 *
 * Confirms with the server that the session actually PAID before showing
 * anything celebratory; the confirmation call is also the second leg of
 * idempotent order creation, so a slow webhook still yields the Order.
 * The cart clears exactly once, on confirmed payment. An unpaid or
 * still-processing session shows a pending state with a retry.
 */
export function StoreSuccessPage({ onNavigate }: Props) {
  const { t } = useTranslation('storeCheckout');
  const { mode } = useTheme();
  const clear = useCartStore((s) => s.clear);
  const itemCount = useCartStore(selectItemCount);

  // One-shot read at mount; the query param is navigation intent.
  const sessionId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('session_id') ?? '';
  }, []);

  const confirmQ = useQuery({
    queryKey: ['store', 'checkout-confirm', sessionId],
    queryFn: () => confirmStoreCheckout(sessionId),
    enabled: sessionId.length > 0,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const paid = confirmQ.data?.paid === true;
  useEffect(() => {
    if (paid) clear();
  }, [paid, clear]);

  return (
    <div className={styles.store} data-store-theme={mode}>
      <header className={`${styles.head} ${styles.headSolid}`}>
        <button type="button" className={styles.headBtn} aria-label={t('success.back')} onClick={() => onNavigate('/store')}>
          <ArrowLeft size={18} />
        </button>
        <span className={`${styles.headTitle} ${styles.headTitleShown}`}>{t('success.title')}</span>
        <span className={styles.headActions}>
          <button type="button" className={styles.headBtn} aria-label={t('success.myOrders')} onClick={() => onNavigate('/store/orders')}>
            <Package size={18} />
          </button>
          <button type="button" className={styles.headBtn} aria-label={t('success.cart')} onClick={() => onNavigate('/store/cart')}>
            <ShoppingCart size={18} />
            {itemCount > 0 && <span className={styles.headCartCount}>{itemCount}</span>}
          </button>
        </span>
      </header>

      <div className={styles.body}>
        {!sessionId && (
          <div className={styles.emptyBox}>
            <p>{t('success.noSession')}</p>
            <button type="button" className={styles.ghostBtn} onClick={() => onNavigate('/store/cart')}>{t('success.backToCart')}</button>
          </div>
        )}

        {sessionId && confirmQ.isLoading && (
          <div className={styles.emptyBox}>
            <Loader2 size={22} className={styles.checkoutSpin} />
            <p>{t('success.confirming')}</p>
          </div>
        )}

        {sessionId && !confirmQ.isLoading && confirmQ.isError && (
          <div className={styles.errorBox}>
            {t('success.confirmError')}
          </div>
        )}

        {sessionId && confirmQ.data && !paid && (
          <div className={styles.emptyBox}>
            <p>
              {t('success.pending')}
            </p>
            <button type="button" className={styles.ghostBtn} onClick={() => void confirmQ.refetch()}>
              {t('success.checkAgain')}
            </button>
          </div>
        )}

        {paid && confirmQ.data && (
          <div className={styles.successBox}>
            <CheckCircle2 size={44} className={styles.successIcon} aria-hidden />
            <h1 className={styles.successTitle}>{t('success.placed')}</h1>
            {confirmQ.data.order_number && (
              <p className={styles.successOrder}>{t('success.orderNumber', { number: confirmQ.data.order_number })}</p>
            )}
            {typeof confirmQ.data.total_cents === 'number' && (
              <p className={styles.successTotal}>
                {formatCents(confirmQ.data.total_cents)} <span className={styles.usd}>USD</span>
              </p>
            )}
            <p className={styles.successNote}>
              {t('success.shipNote')}
            </p>
            <button type="button" className={styles.sheetAdd} onClick={() => onNavigate('/store')}>
              {t('success.backToStore')}
            </button>
            <button type="button" className={styles.sheetViewCart} onClick={() => onNavigate('/store/orders')}>
              <Package size={15} /> {t('success.viewOrders')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
